import { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "../lib/api";
import { useRealtime } from "../lib/useRealtime";

const TaskContext = createContext(null);

const CACHE_KEY = "flowtasks_tasks_cache";

export function TaskProvider({ children, isAuthed }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load cache from localStorage (offline fallback only)
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) setTasks(JSON.parse(cached));
    } catch {}
  }, []);

  // Persist cache for offline access
  useEffect(() => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(tasks));
    } catch {}
  }, [tasks]);

  const refresh = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setTasks(data || []);
    } catch (e) {
      // keep cache
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  useEffect(() => {
    if (isAuthed) refresh();
    else setTasks([]);
  }, [isAuthed, refresh]);

  // Handle realtime events from the websocket
  const { status: wsStatus } = useRealtime((event) => {
    if (!event?.type) return;
    if (event.type === "task_created" && event.task) {
      setTasks((prev) => {
        if (prev.some((t) => t.id === event.task.id)) return prev;
        return [event.task, ...prev];
      });
    } else if (event.type === "task_updated" && event.task) {
      setTasks((prev) => prev.map((t) => (t.id === event.task.id ? event.task : t)));
    } else if (event.type === "task_deleted" && event.task?.id) {
      setTasks((prev) => prev.filter((t) => t.id !== event.task.id));
    } else if (event.type === "reminder") {
      // Trigger a notifications refresh elsewhere
      window.dispatchEvent(new CustomEvent("flowtasks:reminder", { detail: event }));
    }
  });

  // Auto-sync every 60s when authed, online and websocket is NOT open
  useEffect(() => {
    if (!isAuthed) return;
    const id = setInterval(() => {
      if (navigator.onLine && wsStatus !== "open") refresh();
    }, 60000);
    return () => clearInterval(id);
  }, [isAuthed, refresh, wsStatus]);

  const createTask = async (taskInput) => {
    const { data } = await api.post("/tasks", taskInput);
    setTasks((prev) => {
      if (prev.some((t) => t.id === data.id)) return prev;
      return [data, ...prev];
    });
    return data;
  };

  const updateTask = async (id, updates) => {
    const { data } = await api.put(`/tasks/${id}`, updates);
    setTasks((prev) => prev.map((t) => (t.id === id ? data : t)));
    return data;
  };

  const deleteTask = async (id) => {
    await api.delete(`/tasks/${id}`);
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const setStatus = async (id, status) => updateTask(id, { status });

  return (
    <TaskContext.Provider
      value={{ tasks, loading, refresh, createTask, updateTask, deleteTask, setStatus, wsStatus }}
    >
      {children}
    </TaskContext.Provider>
  );
}

export const useTasks = () => useContext(TaskContext);
